import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../../utils/api';
import { validators } from '../../utils/validation';
import { toast } from '../ui/sonner';
import AddressVerifier, { flattenAddress } from '../AddressVerifier';
import { 
  Building2, Shield, Upload, CheckCircle, Clock, AlertCircle, X,
  FileText, MapPin, Users, Mail, Phone, Globe, Camera, RefreshCw,
  ChevronRight, Lock, Unlock, Award, Image, Trash2, Plus, Send
} from 'lucide-react';

// Country Tax ID Configuration (Employer Identification Number for US)
const COUNTRY_TAX_ID_CONFIG = {
  US: { name: 'Employer Identification Number (EIN)', length: 9, format: 'XX-XXXXXXX', type: 'numeric', flag: '🇺🇸', country: 'United States' },
  GB: { name: 'National Insurance Number (NINO)', length: 9, format: 'AA123456A', type: 'alphanumeric', flag: '🇬🇧', country: 'United Kingdom' },
  GH: { name: 'Ghana Card (National ID)', length: 15, format: 'GHA-XXXXXXXXX-X', type: 'alphanumeric', flag: '🇬🇭', country: 'Ghana' },
  DE: { name: 'Tax ID (Steueridentifikationsnummer)', length: 11, format: '11 digits', type: 'numeric', flag: '🇩🇪', country: 'Germany' },
  FR: { name: 'INSEE / NIR (Social Security)', length: 15, format: '15 digits', type: 'numeric', flag: '🇫🇷', country: 'France' },
  CA: { name: 'Social Insurance Number (SIN)', length: 9, format: '9 digits', type: 'numeric', flag: '🇨🇦', country: 'Canada' },
  AU: { name: 'Tax File Number (TFN)', length: 9, format: '8-9 digits', type: 'numeric', flag: '🇦🇺', country: 'Australia' },
  IN: { name: 'Aadhaar Number', length: 12, format: '12 digits', type: 'numeric', flag: '🇮🇳', country: 'India' },
  ZA: { name: 'ID Number', length: 13, format: '13 digits', type: 'numeric', flag: '🇿🇦', country: 'South Africa' },
  NG: { name: 'National Identification Number (NIN)', length: 11, format: '11 digits', type: 'numeric', flag: '🇳🇬', country: 'Nigeria' },
  KE: { name: 'National ID', length: 8, format: '8 digits', type: 'numeric', flag: '🇰🇪', country: 'Kenya' },
  SE: { name: 'Personnummer', length: 12, format: '10-12 digits', type: 'numeric', flag: '🇸🇪', country: 'Sweden' },
  NO: { name: 'Fødselsnummer', length: 11, format: '11 digits', type: 'numeric', flag: '🇳🇴', country: 'Norway' },
  DK: { name: 'CPR Number', length: 10, format: '10 digits', type: 'numeric', flag: '🇩🇰', country: 'Denmark' },
  ES: { name: 'DNI/NIE', length: 9, format: '8 digits + letter', type: 'alphanumeric', flag: '🇪🇸', country: 'Spain' },
  IT: { name: 'Codice Fiscale', length: 16, format: '16 alphanumeric', type: 'alphanumeric', flag: '🇮🇹', country: 'Italy' },
  NL: { name: 'BSN (Burgerservicenummer)', length: 9, format: '9 digits', type: 'numeric', flag: '🇳🇱', country: 'Netherlands' },
  CH: { name: 'AHV Number', length: 13, format: '13 digits', type: 'numeric', flag: '🇨🇭', country: 'Switzerland' },
  BR: { name: 'CPF (Cadastro de Pessoas Físicas)', length: 11, format: '11 digits', type: 'numeric', flag: '🇧🇷', country: 'Brazil' },
  MX: { name: 'CURP', length: 18, format: '18 alphanumeric', type: 'alphanumeric', flag: '🇲🇽', country: 'Mexico' },
  CN: { name: 'Resident ID Number', length: 18, format: '18 digits', type: 'numeric', flag: '🇨🇳', country: 'China' },
  JP: { name: 'My Number (Individual Number)', length: 12, format: '12 digits', type: 'numeric', flag: '🇯🇵', country: 'Japan' },
};

// Helper: render an address that may be a string OR a structured object
// (some legacy locations have address stored as {street, city, state, zip}).
function formatAddress(addr) {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  if (typeof addr === 'object') {
    const parts = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean);
    return parts.join(', ');
  }
  return String(addr);
}

export default function AgencySetup({ onClose, onVerificationComplete, embedded = false }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  const [agencyProfile, setAgencyProfile] = useState({
    company_name: '',
    business_type: 'llc',
    industry: '',
    tax_id_country: 'US',
    tax_id: '',
    website: '',
    phone: '',
    email: '',
    logo_url: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA'
    },
    description: '',
    employee_count: '',
    founded_year: '',
    agency_license_number: '',
    workers_managed_count: '',
  });

  const [documents, setDocuments] = useState({
    staffing_license: null,
    workers_comp_insurance: null,
    liability_insurance: null,
    background_check_policy: null,
  });

  const [locations, setLocations] = useState([]);
  const emptyAddress = { primary_line: '', secondary_line: '', city: '', state: '', zip_code: '' };
  const [newLocation, setNewLocation] = useState({ name: '', address: emptyAddress, is_primary: false });
  const [locationVerification, setLocationVerification] = useState(null);

  useEffect(() => {
    loadVerificationStatus();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const response = await apiCall('/api/agent/verification/status');
      setVerificationStatus(response.verification);
      if (response.company_profile) {
        setAgencyProfile(prev => ({ ...prev, ...response.company_profile }));
      }
      // iter77 doc-persistence bug fix (Feb 20, 2026): hydrate uploaded
      // documents from the server so users don't see an empty upload panel
      // every time they re-open the setup wizard.
      if (response.verification && response.verification.documents) {
        setDocuments(prev => ({ ...prev, ...response.verification.documents }));
      }
      if (response.locations) {
        setLocations(response.locations);
      }
    } catch (error) {
      console.error('Failed to load verification status:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/logo/upload`, {
        method: 'POST',
        // iter77 Phase B: cookie-based auth, no Bearer header needed.
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setAgencyProfile(prev => ({ ...prev, logo_url: data.logo_url }));
        toast.success('Logo uploaded successfully!');
      } else {
        throw new Error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!agencyProfile.company_name || !agencyProfile.industry || !agencyProfile.phone || !agencyProfile.agency_license_number || !agencyProfile.tax_id) {
      toast.error('Please fill in all required fields (Agency Name, License #, Industry, Phone, and Tax ID / EIN)');
      return;
    }
    const vErr =
      validators.companyName(agencyProfile.company_name) ||
      validators.phone(agencyProfile.phone) ||
      validators.alphanumericId(agencyProfile.agency_license_number);
    if (vErr) {
      toast.error(vErr);
      return;
    }
    
    setLoading(true);
    try {
      await apiCall('/api/agent/agency-profile', {
        method: 'POST',
        body: JSON.stringify(agencyProfile)
      });
      toast.success('Company profile saved!');
      setStep(2);
    } catch (error) {
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/verification/upload`, {
        method: 'POST',
        // iter77 Phase B: cookie-based auth, no Bearer header needed.
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setDocuments(prev => ({ ...prev, [docType]: data.file_url }));
        toast.success(`${docType.replace('_', ' ')} uploaded successfully!`);
      } else {
        throw new Error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleAddLocation = async () => {
    const a = newLocation.address || {};
    if (!newLocation.name || !a.primary_line || !a.city || !a.state || !a.zip_code) {
      toast.error('Please enter location name and a complete address');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: newLocation.name,
        address: flattenAddress(a),
        address_components: a,
        is_primary: newLocation.is_primary,
        ...(locationVerification && {
          address_verified: locationVerification.status === 'ok' || locationVerification.status === 'warn',
          address_deliverability: locationVerification.deliverability,
          address_lob_id: locationVerification.lob_id,
          address_lat: locationVerification.lat,
          address_lng: locationVerification.lng,
        }),
      };
      const response = await apiCall('/api/agent/locations', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      setLocations(prev => [...prev, response.location]);
      setNewLocation({ name: '', address: emptyAddress, is_primary: false });
      setLocationVerification(null);
      toast.success('Location added!');
    } catch (error) {
      toast.error(error.message || 'Failed to add location');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVerification = async () => {
    setLoading(true);
    try {
      await apiCall('/api/agent/verification/submit', {
        method: 'POST',
        body: JSON.stringify({ documents })
      });
      toast.success('Verification submitted! We will review your documents shortly.');
      loadVerificationStatus();
      onVerificationComplete?.();
    } catch (error) {
      toast.error(error.message || 'Failed to submit verification');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    const status = verificationStatus?.status || 'not_started';
    const badges = {
      not_started: { bg: 'bg-slate-100', text: 'text-slate-700', icon: Clock, label: 'Not Started' },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Pending Review' },
      under_review: { bg: 'bg-blue-100', text: 'text-blue-700', icon: RefreshCw, label: 'Under Review' },
      verified: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Verified' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'Rejected' }
    };
    const badge = badges[status] || badges.not_started;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const steps = [
    { id: 1, title: 'Company Profile', icon: <Building2 className="w-5 h-5" /> },
    { id: 2, title: 'Documents', icon: <FileText className="w-5 h-5" /> },
    { id: 3, title: 'Locations', icon: <MapPin className="w-5 h-5" /> },
    { id: 4, title: 'Verification', icon: <Shield className="w-5 h-5" /> }
  ];

  const industries = [
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'retail', label: 'Retail' },
    { value: 'construction', label: 'Construction' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'events', label: 'Events & Entertainment' },
    { value: 'technology', label: 'Technology' },
    { value: 'education', label: 'Education' },
    { value: 'finance', label: 'Finance' },
    { value: 'other', label: 'Other' }
  ];

  const businessTypes = [
    { value: 'llc', label: 'LLC' },
    { value: 'corporation', label: 'Corporation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'sole_proprietor', label: 'Sole Proprietor' },
    { value: 'nonprofit', label: 'Non-Profit' }
  ];

  return (
    <div className={embedded
      ? 'w-full'
      : 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4'}>
      <div className={embedded
        ? 'bg-white rounded-2xl shadow-2xl w-full overflow-hidden'
        : 'bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden'}>
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 via-purple-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Building2 className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Agency Setup</h2>
                <p className="text-white/80">Complete your staffing agency profile to start managing workers and posting jobs</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge()}
              {!embedded && (
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition" data-testid="close-agency-setup">
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6 px-4">
            {steps.map((s, idx) => (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => setStep(s.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition ${
                    step === s.id
                      ? 'bg-white/30 text-white'
                      : step > s.id
                      ? 'bg-white/10 text-white/80'
                      : 'text-white/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step >= s.id ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    {step > s.id ? <CheckCircle className="w-5 h-5" /> : s.icon}
                  </div>
                  <span className="hidden md:inline font-medium">{s.title}</span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-white/40' : 'bg-white/10'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className={embedded ? 'p-6' : 'p-6 overflow-y-auto max-h-[60vh]'}>
          {/* Step 1: Company Profile */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Logo Upload Section */}
              <div className="flex items-center gap-6 p-5 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                <div className="relative">
                  {agencyProfile.logo_url ? (
                    <div className="relative">
                      <img 
                        src={agencyProfile.logo_url} 
                        alt="Company logo" 
                        className="w-24 h-24 rounded-xl object-cover border-2 border-white shadow-lg"
                      />
                      <button
                        onClick={() => setAgencyProfile(prev => ({ ...prev, logo_url: '' }))}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => logoInputRef.current?.click()}
                      className="w-24 h-24 rounded-xl bg-white border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 hover:bg-teal-50 transition"
                    >
                      {uploadingLogo ? (
                        <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
                      ) : (
                        <>
                          <Image className="w-8 h-8 text-slate-400" />
                          <span className="text-xs text-slate-500 mt-1">Add Logo</span>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800 text-lg">Agency Logo</h3>
                  <p className="text-sm text-slate-500 mt-1">Upload your agency logo. Recommended size: 200x200px. Max 5MB.</p>
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    className="mt-3 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Logo
                  </button>
                </div>
              </div>

              {/* Company Details */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Agency Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={agencyProfile.company_name}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, company_name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    placeholder="Enter your agency name"
                    data-testid="agency-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Agency License # <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={agencyProfile.agency_license_number || ''}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, agency_license_number: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    placeholder="State-issued staffing/employment license"
                    data-testid="agency-license-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={agencyProfile.business_type}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, business_type: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition appearance-none cursor-pointer"
                    data-testid="business-type-select"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  >
                    {businessTypes.map(type => (
                      <option key={type.value} value={type.value} className="py-2 text-slate-900">
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={agencyProfile.industry}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, industry: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition appearance-none cursor-pointer"
                    data-testid="industry-select"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  >
                    <option value="" className="text-slate-400">Select your industry</option>
                    {industries.map(ind => (
                      <option key={ind.value} value={ind.value} className="py-2 text-slate-900">
                        {ind.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Tax ID / National ID <span className="text-red-500">*</span>
                  </label>
                  
                  {/* Country Selection */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-500 mb-1">Country</label>
                    <select
                      value={agencyProfile.tax_id_country}
                      onChange={(e) => setAgencyProfile({ 
                        ...agencyProfile, 
                        tax_id_country: e.target.value,
                        tax_id: '' // Reset tax ID when country changes
                      })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    >
                      {Object.entries(COUNTRY_TAX_ID_CONFIG).map(([code, config]) => (
                        <option key={code} value={code}>
                          {config.flag} {config.country}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Tax ID Input */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      {COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.name || 'Tax ID'}
                    </label>
                    <input
                      type="text"
                      value={agencyProfile.tax_id}
                      onChange={(e) => {
                        const config = COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country];
                        let value = e.target.value;
                        
                        // Apply validation based on country config
                        if (config?.type === 'numeric') {
                          value = value.replace(/[^0-9-]/g, '');
                        }
                        
                        // Limit to max length (allow for dashes/separators)
                        const maxLen = config?.length + 4 || 20;
                        if (value.length <= maxLen) {
                          setAgencyProfile({ ...agencyProfile, tax_id: value });
                        }
                      }}
                      maxLength={COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.length + 4 || 20}
                      className={`w-full px-4 py-3 bg-white border rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition ${
                        agencyProfile.tax_id && 
                        agencyProfile.tax_id.replace(/[^a-zA-Z0-9]/g, '').length === COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.length
                          ? 'border-green-400 bg-green-50' 
                          : 'border-slate-300'
                      }`}
                      placeholder={COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.format || 'Enter ID'}
                      data-testid="tax-id-input"
                    />
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-slate-500">
                        Format: {COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.format} ({COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.length} characters)
                      </p>
                      {agencyProfile.tax_id && (
                        <p className={`text-xs ${
                          agencyProfile.tax_id.replace(/[^a-zA-Z0-9]/g, '').length === COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.length
                            ? 'text-green-600' : 'text-amber-600'
                        }`}>
                          {agencyProfile.tax_id.replace(/[^a-zA-Z0-9]/g, '').length} / {COUNTRY_TAX_ID_CONFIG[agencyProfile.tax_id_country]?.length}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Website</label>
                  <input
                    type="url"
                    value={agencyProfile.website}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, website: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    placeholder="https://yourcompany.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={agencyProfile.phone}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Business Address <span className="text-red-500">*</span>
                </label>
                <AddressVerifier
                  value={{
                    primary_line: agencyProfile.address.street || '',
                    secondary_line: agencyProfile.address.suite || '',
                    city: agencyProfile.address.city || '',
                    state: agencyProfile.address.state || '',
                    zip_code: agencyProfile.address.zip || '',
                  }}
                  onChange={(a) => setAgencyProfile({
                    ...agencyProfile,
                    address: {
                      ...agencyProfile.address,
                      street: a.primary_line,
                      suite: a.secondary_line,
                      city: a.city,
                      state: a.state,
                      zip: a.zip_code,
                    },
                  })}
                  colorScheme="teal"
                  required
                  testIdPrefix="agency-setup-business-address"
                  onVerificationResult={(r) => setAgencyProfile((p) => ({
                    ...p,
                    address: {
                      ...p.address,
                      verified: r.status === 'ok' || r.status === 'warn',
                      deliverability: r.deliverability,
                      lob_id: r.lob_id,
                      lat: r.lat,
                      lng: r.lng,
                    },
                  }))}
                />
              </div>

              {/* Agency-specific extras */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Workers currently managed
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={agencyProfile.workers_managed_count || ''}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, workers_managed_count: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    placeholder="e.g. 25"
                    data-testid="agency-workers-count-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Year founded</label>
                  <input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={agencyProfile.founded_year || ''}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, founded_year: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    placeholder="e.g. 2015"
                  />
                </div>
              </div>


              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Agency Description</label>
                <textarea
                  value={agencyProfile.description}
                  onChange={(e) => setAgencyProfile({ ...agencyProfile, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition resize-none"
                  placeholder="Tell us about your staffing agency — specialties, years of experience, industries served..."
                />
              </div>
            </div>
          )}

          {/* Step 2: Documents */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-amber-900">Document Requirements</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Please upload clear, readable copies of your business documents. Accepted formats: PDF, JPG, PNG. Max 10MB per file.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                {[
                  { key: 'staffing_license', label: 'State Staffing/Employment License', desc: 'Your official state-issued staffing or employment agency license' },
                  { key: 'workers_comp_insurance', label: 'Workers\' Compensation Insurance', desc: 'Certificate showing active workers\' comp coverage' },
                  { key: 'liability_insurance', label: 'General Liability Insurance', desc: 'Proof of business liability insurance coverage' },
                  { key: 'background_check_policy', label: 'Background Check Policy', desc: 'Document outlining your worker background-check process' },
                ].map((doc) => (
                  <div
                    key={doc.key}
                    className={`p-5 border-2 rounded-xl transition ${
                      documents[doc.key]
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-slate-200 bg-white hover:border-teal-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${documents[doc.key] ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                          {documents[doc.key] ? (
                            <CheckCircle className="w-6 h-6 text-emerald-600" />
                          ) : (
                            <FileText className="w-6 h-6 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">{doc.label}</h4>
                          <p className="text-sm text-slate-500">{doc.desc}</p>
                        </div>
                      </div>
                      <label className={`px-4 py-2 rounded-xl font-medium cursor-pointer transition flex items-center gap-2 ${
                        documents[doc.key]
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}>
                        {uploadingDoc ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        {documents[doc.key] ? 'Replace' : 'Upload'}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocumentUpload(doc.key, e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Locations */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-900">Add Your Work Locations</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Add all the locations where your workers will be assigned. You can add multiple locations.
                    </p>
                  </div>
                </div>
              </div>

              {/* Existing Locations */}
              {locations.length > 0 && (
                <div className="space-y-3">
                  {locations.map((loc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-100 rounded-lg">
                          <MapPin className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-800">{loc.name}</h4>
                          <p className="text-sm text-slate-500">{formatAddress(loc.address)}</p>
                        </div>
                      </div>
                      {loc.is_primary && (
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full">
                          Primary
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Location */}
              <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add New Location
                </h4>
                <div className="space-y-4">
                  <input
                    type="text"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
                    placeholder="Location Name (e.g., Main Office)"
                    data-testid="agency-setup-location-name"
                  />
                  <AddressVerifier
                    value={newLocation.address}
                    onChange={(addr) => setNewLocation({ ...newLocation, address: addr })}
                    colorScheme="teal"
                    required
                    testIdPrefix="agency-setup-location"
                    onVerificationResult={setLocationVerification}
                  />
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <input
                    type="checkbox"
                    id="is_primary"
                    checked={newLocation.is_primary}
                    onChange={(e) => setNewLocation({ ...newLocation, is_primary: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <label htmlFor="is_primary" className="text-sm text-slate-600">Set as primary location</label>
                </div>
                <button
                  onClick={handleAddLocation}
                  disabled={loading}
                  className="mt-4 px-5 py-2.5 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add Location
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Verification */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-teal-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800">Ready for Verification</h3>
                <p className="text-slate-500 mt-2 max-w-md mx-auto">
                  Review your information and submit for verification. Our team will review your documents within 1-2 business days.
                </p>
              </div>

              {/* Summary Cards */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-teal-600" />
                    Company Profile
                  </h4>
                  <p className="text-slate-800 font-medium">{agencyProfile.company_name || 'Not provided'}</p>
                  <p className="text-sm text-slate-500">{agencyProfile.industry || 'Industry not selected'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-teal-600" />
                    Documents
                  </h4>
                  <p className="text-slate-800 font-medium">
                    {Object.values(documents).filter(Boolean).length} of 4 uploaded
                  </p>
                  <p className="text-sm text-slate-500">
                    {Object.values(documents).filter(Boolean).length === 4 ? 'All documents ready' : 'Some documents missing'}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    Locations
                  </h4>
                  <p className="text-slate-800 font-medium">{locations.length} location(s) added</p>
                  <p className="text-sm text-slate-500">
                    {locations.length > 0 ? 'Ready for workers' : 'Add at least one location'}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-teal-600" />
                    Status
                  </h4>
                  {getStatusBadge()}
                </div>
              </div>

              <button
                onClick={handleSubmitVerification}
                disabled={loading || verificationStatus?.status === 'under_review'}
                className="w-full py-4 bg-gradient-to-r from-teal-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : verificationStatus?.status === 'under_review' ? (
                  <>
                    <Clock className="w-5 h-5" />
                    Under Review
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit for Verification
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step < 4 && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <button
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="px-5 py-2.5 text-slate-600 font-medium rounded-xl hover:bg-slate-200 transition disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={step === 1 ? handleSaveProfile : () => setStep(s => s + 1)}
              disabled={loading}
              className="px-6 py-2.5 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              {step === 1 ? 'Save & Continue' : 'Continue'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
